/**
 * FilterSearch - Search input within filter menu
 *
 * Text search field for filtering options or content.
 * Includes search icon and clear button.
 */

import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useRef } from 'react';

import type { FilterSearchProps } from './Filter.types';

export function FilterSearch({
  value,
  onChange,
  placeholder = 'Search...',
  className = '',
}: FilterSearchProps): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClear = () => {
    onChange('');
    inputRef.current?.focus();
  };

  return (
    <div className={`relative ${className}`.trim()} data-testid="filter-search">
      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="
          w-full
          pl-9 pr-8 py-2
          bg-contrast-600
          border border-stroke-default
          rounded-lg
          text-body-lg md:text-body-md text-neutral-100
          placeholder:text-neutral-500
          transition-all duration-150
          hover:border-stroke-strong
          focus:outline-none focus:border-primary-400/50 focus-visible:ring-state-focus-inset
        "
      />
      {value && (
        <button
          type="button"
          onClick={handleClear}
          className="
            absolute right-2 top-1/2 -translate-y-1/2
            p-1
            rounded
            text-neutral-500 hover:text-neutral-300
            hover:bg-lift-3
            transition-colors duration-150
            focus:outline-none
          "
          aria-label="Clear search"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export default FilterSearch;
